import { useTranslation } from "react-i18next";
import { ConfigSwitch } from "./ConfigSwitch";

type SectionToggleCardBaseProps = {
  icon: string;
  titleKey: string;
  hiddenHintKey: string;
  visibleHintKey: string;
  roleLabels: Record<string, string>;
  visible: boolean;
  hiddenHintClassName?: string;
  /** Shows the "client override" chip next to the title (live editor only). */
  overrideChip?: boolean;
};

type SectionToggleCardEditableProps = SectionToggleCardBaseProps & {
  variant: "editable";
  switchId: string;
  ariaLabelKey: string;
  onChange: (value: boolean) => void;
};

type SectionToggleCardReadonlyProps = SectionToggleCardBaseProps & {
  variant: "readonly";
};

export type SectionToggleCardProps =
  | SectionToggleCardEditableProps
  | SectionToggleCardReadonlyProps;

/**
 * One row of the Client Configuration "section" list (training, goals,
 * femaleCycleTracking, yourPlans, ...): icon + title, a hidden/visible hint,
 * and either an editable `ConfigSwitch` or a readonly on/hidden chip. Shared
 * across the live per-athlete editor, the template editor and the read-only
 * template preview, which previously hand-duplicated this same JSX once per
 * section per file. `training` and `goals` in the live editor keep their own
 * hand-written card - they carry extra widget-blocked/coach-no-data banner
 * logic that doesn't apply anywhere else, and folding it in here would add
 * more branching than the dedupe removes.
 */
export function SectionToggleCard(props: SectionToggleCardProps) {
  const { t } = useTranslation("athlete-portal");
  const {
    icon,
    titleKey,
    hiddenHintKey,
    visibleHintKey,
    roleLabels,
    visible,
    hiddenHintClassName,
    overrideChip,
  } = props;

  return (
    <div className="client-config-card">
      <div className="client-config-card__left">
        <div className="client-config-card__title">
          <span>
            {icon} {t(titleKey)}
          </span>
          {overrideChip && (
            <span className="chip warning">
              {t("clientConfiguration.chips.clientOverride", roleLabels)}
            </span>
          )}
        </div>
        {!visible && (
          <span className={hiddenHintClassName}>
            {t(hiddenHintKey, roleLabels)}
          </span>
        )}
        {visible && <span>{t(visibleHintKey, roleLabels)}</span>}
      </div>
      <div className="client-config-card__right">
        {props.variant === "editable" ? (
          <ConfigSwitch
            id={props.switchId}
            ariaLabel={t(props.ariaLabelKey, roleLabels)}
            checked={visible}
            onChange={props.onChange}
          />
        ) : (
          <span className="chip info">
            {visible
              ? t("clientConfiguration.chips.on")
              : t("clientConfiguration.chips.hidden")}
          </span>
        )}
      </div>
    </div>
  );
}
